import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Loader2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import api from '@/services/api';

// Logo SVG Component
const Logo = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 200 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 25 L25 10 L40 25 L25 18 L10 25Z" fill="#1B4F72" />
    <path d="M25 18 L40 25 L25 40 L25 32 L15 25 L25 18Z" fill="#D4A03A" fillOpacity="0.9" />
    <text x="50" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="700" fill="#1B4F72">GRC</text>
    <text x="95" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="400" fill="#64748B">educators</text>
    <text x="50" y="45" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="8" fontWeight="500" fill="#D4A03A" letterSpacing="0.1em">YOUR PATH TO EXCELLENCE</text>
  </svg>
);

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const frontendUrl = window.location.origin;
      await api.post(`/auth/forgot-password?email=${encodeURIComponent(email)}&frontend_url=${encodeURIComponent(frontendUrl)}`);
      setSuccess(true);
    } catch (err) {
      setError('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent py-20 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-10">
          <Link to="/">
            <Logo className="h-12 w-auto mx-auto" />
          </Link>
        </div>

        <Card className="border-zinc-200 shadow-xl rounded-2xl overflow-hidden">
          <CardContent className="p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-zinc-950 mb-3">
                  Check Your Email
                </h2>
                <p className="text-zinc-600 mb-6">
                  If an account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
                </p>
                <Link to="/login">
                  <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl px-6">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="font-heading text-2xl font-bold text-zinc-950 mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-zinc-500">
                    No worries! Enter your email and we'll send you reset instructions.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <Alert className="bg-red-50 border-red-200 rounded-xl">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <AlertDescription className="text-red-700">{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-700 font-medium">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-14 rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 text-base"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-14 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold text-base rounded-xl"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </form>

                <div className="mt-8 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-zinc-900 hover:text-emerald-700 font-medium"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
